import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, Edit, Loader2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ManageCategory = () => {
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(true);
    
    // Form State updated with specType and specValue
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [image, setImage] = useState(null);
    const [features, setFeatures] = useState([{ specType: '', specValue: '' }]);

    useEffect(() => {  
        fetchCategories(); 
    }, []);

    const fetchCategories = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/viewcategories`, {
                headers: { authorization: `Bearer ${token}` }
            });
            setCategories(res.data.categories);
        } catch (err) {
            console.error("Fetch Error:", err);
            handleAuthError(err);
        } finally {
            setFetchLoading(false);
        }
    };

    const handleAuthError = (err) => {
        if (err.response?.status === 401 || err.response?.status === 400) {
            alert("Session Expired. Please login again.");
            localStorage.removeItem('adminToken');
            window.location.href = "/admin-login";
        }
    };

    // Updated Feature logic for specType/specValue
    const addFeature = () => setFeatures([...features, { specType: '', specValue: '' }]);
    
    const removeFeature = (index) => {
        const list = [...features];
        list.splice(index, 1);
        setFeatures(list);
    };

    const handleFeatureChange = (index, e) => {
        const { name, value } = e.target;
        const list = [...features];
        list[index][name] = value;
        setFeatures(list);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        const token = localStorage.getItem('adminToken');
        const formData = new FormData();
        formData.append('categoryName', name);
        formData.append('categoryDescription', description);
        formData.append('image', image);
        // This will now send an array of objects with specType and specValue
        formData.append('features', JSON.stringify(features));

        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/insertcategory`, formData, {
                headers: { 
                    authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data' 
                }
            });
            alert("Category Added Successfully!");
            
            // Reset form
            setName('');
            setDescription('');
            setImage(null);
            setFeatures([{ specType: '', specValue: '' }]);
            fetchCategories(); 
        } catch (err) {
            console.error("Submit Error:", err);
            handleAuthError(err);
        } finally {
            setLoading(false);
        }
    };

    const deleteCategory = async (id) => { 
        if (!window.confirm("Are you sure?")) return;
        try {
            const token = localStorage.getItem('adminToken');
            await axios.delete(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/deletecategory/${id}`, {
                headers: { authorization: `Bearer ${token}` }
            });
            setCategories(categories.filter(cat => cat._id !== id));
        } catch (err) {
            handleAuthError(err);
        }
    };

    if (fetchLoading) return (
        <div className="h-96 flex flex-col items-center justify-center">
            <Loader2 className="animate-spin text-orange-500 mb-4" size={40} />
            <p className="text-slate-400 font-bold uppercase text-xs tracking-widest">Loading Catalog...</p>
        </div>
    );

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <header className="flex justify-between items-end mb-8">
                <div>
                    <h1 className="text-3xl font-black uppercase italic">Manage <span className="text-orange-500">Categories</span></h1>
                    <p className="text-slate-400 text-xs font-bold uppercase tracking-tighter">Inventory & Catalog Control</p>
                </div>
            </header>

            {/* FORM SECTION */}
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 mb-12">
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Category Name</label>
                            <input type="text" value={name} onChange={(e)=>setName(e.target.value)} className="w-full p-4 bg-slate-50 rounded-2xl border-none focus:ring-2 focus:ring-orange-500 transition-all font-medium" placeholder="e.g. Mobile Cranes" required />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Display Image</label>
                            <input type="file" onChange={(e)=>setImage(e.target.files[0])} className="w-full p-3 bg-slate-50 rounded-2xl file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-bold file:bg-orange-50 file:text-orange-700 cursor-pointer" required />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Category Description</label>
                        <textarea value={description} onChange={(e)=>setDescription(e.target.value)} className="w-full p-4 bg-slate-50 rounded-2xl border-none focus:ring-2 focus:ring-orange-500 h-28 font-medium" placeholder="Describe the machine category..." required />
                    </div>

                    {/* FEATURES COMPONENT */}
                    <div className="space-y-4 bg-slate-50/50 p-6 rounded-3xl border border-dashed border-slate-200">
                        <div className="flex justify-between items-center">
                            <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400">Technical Specs</h3>
                            <button type="button" onClick={addFeature} className="flex items-center gap-2 text-[10px] font-black bg-slate-900 text-white px-4 py-2 rounded-full hover:bg-orange-500 transition-all uppercase tracking-widest">
                                <Plus size={14} /> Add Row
                            </button>
                        </div>
                        {features.map((item, index) => (
                            <div key={index} className="flex gap-4 items-center">
                                <input name="specType" placeholder="Feature (e.g. Mobility)" value={item.specType} onChange={(e)=>handleFeatureChange(index, e)} className="flex-1 p-3 bg-white rounded-xl text-sm border border-slate-100 shadow-sm" />
                                <input name="specValue" placeholder="Value (e.g. High-Speed)" value={item.specValue} onChange={(e)=>handleFeatureChange(index, e)} className="flex-1 p-3 bg-white rounded-xl text-sm border border-slate-100 shadow-sm" />
                                {features.length > 1 && (
                                    <button type="button" onClick={() => removeFeature(index)} className="w-10 h-10 flex items-center justify-center bg-red-50 text-red-500 rounded-xl hover:bg-red-500 hover:text-white transition-all"><X size={18}/></button>
                                )}
                            </div>
                        ))}
                    </div>

                    <button type="submit" disabled={loading} className="w-full py-4 bg-orange-500 text-white font-black uppercase tracking-widest rounded-2xl hover:bg-slate-900 transition-all flex justify-center items-center gap-2 shadow-lg shadow-orange-500/20 disabled:opacity-50">
                        {loading ? <Loader2 className="animate-spin"/> : "Create Category"}
                    </button>
                </form>
            </div>

            {/* TABLE SECTION */}
            <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-slate-900 text-white text-[10px] uppercase tracking-widest">
                        <tr>
                            <th className="p-5 text-center">ID</th>
                            <th className="p-5">Visual</th>
                            <th className="p-5">Details</th>
                            <th className="p-5">Specs</th>
                            <th className="p-5 text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {categories.map((cat) => (
                            <tr key={cat._id} className="hover:bg-orange-50/10 transition-all group text-sm">
                                <td className="p-5 text-center font-bold text-slate-400">#{cat.categoryId}</td>
                                <td className="p-5">
                                    <img src={cat.imageId} alt={cat.categoryName} className="w-16 h-12 rounded-xl object-cover bg-slate-100 shadow-sm" />
                                </td>
                                <td className="p-5">
                                    <div className="flex flex-col">
                                        <span className="font-black uppercase text-slate-900 tracking-tight">{cat.categoryName}</span>
                                        <span className="text-slate-400 text-[11px] line-clamp-1">{cat.categoryDescription}</span>
                                    </div>
                                </td>
                                <td className="p-5">
                                    <div className="flex flex-wrap gap-1">
                                        {cat.features?.slice(0, 2).map((f, i) => (
                                            <span key={i} className="text-[9px] bg-slate-100 px-2 py-0.5 rounded font-bold uppercase text-slate-500">
                                                {f.specType}: {f.specValue}
                                            </span>
                                        ))}
                                    </div>
                                </td>
                                <td className="p-5">
                                    <div className="flex justify-center gap-2">
                                        <button onClick={() => navigate(`/admin/update-category/${cat._id}`)} className="w-9 h-9 flex items-center justify-center bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-600 hover:text-white transition-all">
                                            <Edit size={16}/>
                                        </button>
                                        <button onClick={() => deleteCategory(cat._id)} className="w-9 h-9 flex items-center justify-center bg-red-50 text-red-600 rounded-xl hover:bg-red-600 hover:text-white transition-all">
                                            <Trash2 size={16}/>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ManageCategory;